import {
  getDisplayName,
  getProfileType,
  PET_SPECIES,
  PROFILE_TYPE,
} from './companionProfiles';
import { formatLifespan, formatPersonDateRange, formatPersonLifespan } from './dateDisplay';

/**
 * User-facing labels for tree collaboration roles (avoid the word "owner" in UI).
 */
export function getTreeCollaborationRoleLabel(role) {
  if (role === 'owner') return 'Tree creator';
  if (role === 'editor') return 'Editor';
  if (role === 'viewer') return 'Viewer';
  return 'Member';
}

function capitalizeSpecies(species) {
  if (!species) return '';
  const match = PET_SPECIES.find((s) => s.value === species);
  if (match) return match.label;
  const text = String(species).trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Line 2 for list cards — one primary life/date (or pet species) line only. */
export function personListDateLine(person) {
  if (!person) return '';

  const type = getProfileType(person);

  if (type === PROFILE_TYPE.PET) {
    const lifespan = formatPersonLifespan(person);
    if (lifespan) return lifespan;
    return formatPetSpeciesLine(person);
  }

  if (type === PROFILE_TYPE.COMPANION) {
    const lifespan = formatPersonLifespan(person);
    if (lifespan) return lifespan;
    if (person.family_connection?.trim()) return person.family_connection.trim();
    return '';
  }

  if (person.birth_date && person.death_date) {
    return formatPersonDateRange(person);
  }
  if (person.birth_date) {
    return formatLifespan(person.birth_date, null);
  }
  if (person.birth_year && !person.birth_date) {
    if (person.death_year && !person.death_date) {
      return `${person.birth_year} – ${person.death_year}`;
    }
    return `Born ${person.birth_year}`;
  }
  if (person.death_date) {
    return formatLifespan(null, person.death_date);
  }
  if (person.death_year && !person.death_date) {
    return `Died ${person.death_year}`;
  }

  return '';
}

/** Line 3 for list cards — location only, when available. */
export function personListLocationLine(person) {
  const place = person?.birth_place?.trim();
  return place || '';
}

export function formatPetSpeciesLine(person) {
  if (!person?.species) return '';
  const species = capitalizeSpecies(person.species);
  const breed = person.breed?.trim() ? ` · ${person.breed.trim()}` : '';
  return `${species}${breed}`;
}

/** Single-line subtitle for compact rows (dropdowns) — date first, then location. */
export function personListSubtitle(person) {
  const dateLine = personListDateLine(person);
  if (dateLine) return dateLine;
  return personListLocationLine(person);
}

export { getDisplayName };
